/**
 * CV text normaliser — the single repair layer every ATS score runs through.
 *
 * Real CVs arrive from three routes: pasted text, .docx extraction and PDF
 * extraction. PDF and copy-paste routinely flatten the document into one long
 * line, which silently destroyed the score: the engine's section, bullet,
 * chronology and contact checks are all line-based, so a perfectly good CV
 * could score in the 30s purely because the newlines were lost.
 *
 * This module rebuilds a line structure the engine can read, without changing
 * a single word of the candidate's content.
 */

const SECTION_WORDS = [
  "professional summary","personal summary","career summary","executive summary",
  "profile","personal profile","career objective","objective","summary",
  "work experience","professional experience","employment history","employment",
  "experience","education and training","education","academic qualifications",
  "qualifications","core skills","key skills","technical skills","skills",
  "certifications","certificates","licences","licenses","achievements",
  "key achievements","awards","projects","publications","languages",
  "references","referees","interests","volunteer experience","memberships",
  "professional development","contact","contact details","personal details",
];

const MONTHS =
  "jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t)?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?";

/** True when the text has lost its line structure (typical of PDF extraction). */
export function looksFlattened(text: string): boolean {
  const lines = text.split("\n").filter((l) => l.trim());
  if (!lines.length) return false;
  const words = text.split(/\s+/).filter(Boolean).length;
  if (words < 80) return false;
  const longest = Math.max(...lines.map((l) => l.length));
  const avg = lines.reduce((a, l) => a + l.length, 0) / lines.length;
  return avg > 180 || longest > 400 || lines.length < Math.max(6, words / 60);
}

function reflow(text: string): string {
  let s = text;

  // 1. Bullet glyphs always start a new line.
  s = s.replace(/\s*[\u2022\u25AA\u25CF\u25E6\u2043\u00B7\u2219\u25A0\u25B8\u2023\u002A]\s+/g, "\n• ");

  // 2. Section headers start a new line (and get their own line after).
  const headerAlt = SECTION_WORDS.slice()
    .sort((a, b) => b.length - a.length)
    .join("|");
  s = s.replace(
    new RegExp(`(?<=[^\\n])\\s+(${headerAlt})\\s*:?\\s+`, "gi"),
    (_m, h: string) => `\n${String(h).toUpperCase()}\n`,
  );

  // 3. Date ranges (employment lines) start a new line.
  s = s.replace(
    new RegExp(
      `\\s+((?:${MONTHS})?\\.?\\s*(?:19|20)\\d{2}\\s*(?:-|–|—|to)\\s*(?:present|current|(?:${MONTHS})?\\.?\\s*(?:19|20)\\d{2}))`,
      "gi",
    ),
    "\n$1",
  );

  // 4. Contact details each get their own line.
  s = s.replace(/\s+([\w.+-]+@[\w-]+\.[\w.-]+)/g, "\n$1");
  s = s.replace(/\s+((?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[^\s]+)/gi, "\n$1");
  s = s.replace(/\s+(\+?\d[\d\s\-().]{7,}\d)(?=\s|$)/g, "\n$1");

  // 5. Sentence ends inside a bullet-free block become line breaks so long
  //    paragraphs are not read as one 300-word "bullet".
  s = s.replace(/([.;])\s+(?=[A-Z][a-z])/g, "$1\n");

  return s;
}

/**
 * Cleans and, when needed, rebuilds the line structure of a CV so the scoring
 * engine sees the same document a human (and a real ATS parser) would.
 */
export function normaliseCvText(input: string): string {
  if (!input) return "";
  let s = String(input)
    .replace(/\r\n?/g, "\n")
    .replace(/[\u00A0\u2007\u202F\u200B]/g, " ")
    .replace(/\uFB01/g, "fi")
    .replace(/\uFB02/g, "fl");

  // Repair words split across a line break by PDF hyphenation.
  s = s.replace(/([a-z])-\n([a-z])/g, "$1$2");

  // Drop page furniture that PDF extraction drags in.
  s = s
    .split("\n")
    .filter((l) => !/^\s*(page\s*\d+\s*(of\s*\d+)?|\d+\s*\|\s*page)\s*$/i.test(l))
    .join("\n");

  if (looksFlattened(s)) s = reflow(s);

  s = s
    .split("\n")
    .map((l) =>
      l
        .replace(/[ \t]{2,}/g, " ")
        // Normalise every bullet marker to a single "• ".
        .replace(/^\s*[-–—*\u2022\u25AA\u25CF\u25E6\u00B7\u2219\u25A0\u25B8\u2023]\s+/, "• ")
        .trimEnd(),
    )
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return s;
}
